// #482: Trusted Types bridge for the widget's static, developer-controlled
// HTML sinks.
//
// Under a host CSP `require-trusted-types-for 'script'`, ANY string→HTML sink
// (innerHTML, insertAdjacentHTML, outerHTML, document.write) throws a TypeError
// — which previously crashed mountUi before the launcher ever painted. Several
// of the widget's render paths build large blocks of fully developer-authored
// markup (the form, the success screen, the challenge UI, the attachment-slot
// list, the consent modal). Rewriting every one into createElement chains would
// add hundreds of lines and bundle weight for markup whose only dynamic values
// are already escaped at their interpolation sites.
//
// Instead, route those assignments through a single Trusted Types policy. When
// the host enforces TT, `policy.createHTML(s)` returns a TrustedHTML the setter
// accepts; when TT is absent the raw string is assigned exactly as before.
//
// `createPolicy` itself throws if the host's `trusted-types` directive does not
// allowlist our name (e.g. `trusted-types 'none'`, or a fixed name list that
// omits ours), so the probe is feature-detected AND wrapped in try/catch. If the
// policy can't be created we keep the cached policy null and fall back to a
// guarded raw assignment — correct on hosts WITHOUT enforcement, and on hosts
// WITH enforcement that forbid our policy name the assignment throws and is
// swallowed per-call so one un-renderable block never takes down the mount.
//
// The launcher icon does NOT use this helper — it is DOM-constructed in ui.ts
// so the widget stays reachable even in the worst case (TT enforced + every
// policy name forbidden).

interface TrustedTypePolicy {
  createHTML: (input: string) => unknown;
}
interface TrustedTypeWindow {
  trustedTypes?: {
    createPolicy: (name: string, rules: { createHTML: (s: string) => string }) => TrustedTypePolicy;
  };
}

let ttPolicy: TrustedTypePolicy | null = null;
let ttPolicyResolved = false;

function getTrustedTypesPolicy(): TrustedTypePolicy | null {
  if (ttPolicyResolved) return ttPolicy;
  ttPolicyResolved = true;
  try {
    const tt =
      typeof window !== 'undefined'
        ? (window as unknown as TrustedTypeWindow).trustedTypes
        : undefined;
    if (tt?.createPolicy) {
      // Developer-controlled markup only — createHTML is the identity function.
      // Dynamic values are escaped at their interpolation site, never here.
      ttPolicy = tt.createPolicy('oncatch-widget', { createHTML: (s) => s });
    }
  } catch {
    // Host's `trusted-types` directive forbids this policy name → leave null
    // and let setTrustedHtml fall through to a guarded raw assignment.
    ttPolicy = null;
  }
  return ttPolicy;
}

/**
 * #482: assign static, developer-controlled HTML to an element in a way that
 * survives a host CSP enforcing Trusted Types. Uses the `oncatch-widget` policy
 * when available; otherwise assigns the raw string (correct when TT is not
 * enforced). Wrapped in try/catch so a TT-enforcing host that also forbids our
 * policy name can't throw an uncaught error out of a render path.
 */
export function setTrustedHtml(el: { innerHTML: string }, html: string): void {
  try {
    const policy = getTrustedTypesPolicy();
    el.innerHTML = (policy ? policy.createHTML(html) : html) as string;
  } catch {
    // TT enforced + our policy forbidden, or the setter otherwise rejected the
    // value. The block can't render via innerHTML here; swallow rather than
    // crash. The launcher icon (DOM-built in ui.ts) still paints, so the widget
    // remains reachable even in this worst case.
  }
}

/**
 * #482 test seam — reset the memoized policy probe so a test can simulate a
 * fresh page (e.g. toggling `window.trustedTypes` between cases). Not used in
 * production code paths.
 */
export function __resetTrustedTypesPolicyForTest(): void {
  ttPolicy = null;
  ttPolicyResolved = false;
}

// ============================================================================
// #584: CSP-safe stylesheet attachment
// ============================================================================

/**
 * #584: attach CSS to a shadow root or the host document WITHOUT a `<style>`
 * element.
 *
 * A raw `<style>` is governed by the host page's `style-src-elem`. A site on a
 * strict nonce policy (e.g. Next.js `style-src-elem 'self' 'nonce-…'` with no
 * 'unsafe-inline') refuses the un-nonced element, and the widget cannot read
 * the host's per-request nonce — so a `<style>` can NEVER satisfy such a
 * policy. Constructable stylesheets are applied programmatically and are not
 * governed by `style-src` at all, so they survive any strict CSP.
 *
 * #477 established this for the widget's own shadow root (applyShadowStyles in
 * ui.ts) but converted only that one call site. Three other render paths kept
 * the `<style>` element and were silently unstyled on every strict-CSP host:
 * the annotation overlay (annotation.ts), the screenshot privacy mask
 * (privacy-mask.ts — a real privacy failure, not just cosmetic), and the
 * capture toast's spinner keyframes (ui.ts). This helper is the shared,
 * reusable form so a fourth site can't drift the same way.
 *
 * Differs from applyShadowStyles in two ways that matter here:
 *   - APPENDS to `adoptedStyleSheets` instead of replacing the array, so
 *     adopting the annotation overlay's CSS cannot wipe the widget's own.
 *   - Returns a teardown, because these sheets are transient. Without it,
 *     opening and closing the annotation editor repeatedly would accumulate a
 *     duplicate sheet per open.
 *
 * Falls back to a `<style>` element on engines without constructable
 * stylesheets. Those predate strict-CSP adoption, so the block does not apply
 * there anyway.
 *
 * @param root  A ShadowRoot, or `document` to style the host page itself.
 * @param css   The stylesheet text.
 * @returns A teardown that detaches the styles. Idempotent.
 */
export function adoptStyles(root: ShadowRoot | Document, css: string): () => void {
  try {
    if (
      typeof CSSStyleSheet === 'function' &&
      'replaceSync' in CSSStyleSheet.prototype &&
      Array.isArray(root.adoptedStyleSheets)
    ) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      // Reassign rather than push: in older Chromium the array is frozen.
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      let detached = false;
      return () => {
        if (detached) return;
        detached = true;
        try {
          root.adoptedStyleSheets = root.adoptedStyleSheets.filter((s) => s !== sheet);
        } catch {
          // Root torn down before teardown ran — nothing to detach.
        }
      };
    }
  } catch {
    // Constructor unavailable / threw → fall through to the <style> element.
  }

  const style = document.createElement('style');
  style.textContent = css;
  // A Document has no appendChild target that styles reliably except head;
  // a ShadowRoot takes the element directly.
  const parent =
    root.nodeType === 9
      ? ((root as Document).head ?? (root as Document).documentElement)
      : (root as unknown as ShadowRoot);
  parent.appendChild(style);
  let removed = false;
  return () => {
    if (removed) return;
    removed = true;
    try {
      style.remove();
    } catch {
      // Already detached or document gone — nothing to do.
    }
  };
}

/**
 * #584: resolve the style root for an element mounted inside the widget's
 * shadow tree. Returns the ShadowRoot when the element is inside one (the
 * normal case — the annotation overlay mounts into the widget panel), and
 * `document` when it is in the light DOM (the demo-mount path, and any future
 * caller that mounts outside the shadow root).
 */
export function styleRootFor(el: Node): ShadowRoot | Document {
  const root = el.getRootNode();
  return root instanceof ShadowRoot ? root : document;
}
